// apps/web/src/routes/ProfileSettings.tsx
//
// /settings/profile — edit non-sensitive profile fields. Handle
// change and contact change are step-up gated and live in
// dedicated modals (so the user knows "we're doing a security-
// sensitive thing"). All updates validate the 18+ age gate
// (server-enforced, but the client also re-checks so the user
// gets instant feedback).

import { useEffect, useRef, useState } from 'react';
import { FormField } from '../components/ui/FormField.js';
import { Button } from '../components/ui/Button.js';
import { Alert } from '../components/ui/Alert.js';
import { StepUpModal } from '../components/StepUpModal.js';
import { OtpVerifyPanel } from '../components/OtpVerifyPanel.js';
import { apiGet, apiPut, apiPost, ApiError } from '../lib/apiClient.js';
import { useAuth, useMeOrNull } from '../lib/auth.js';
import type { UserMe, OtpSession } from '../lib/api.js';

const HANDLE_REGEX = /^[a-z][a-z0-9_]{2,29}$/;

function todayMinus18(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10);
}

type HandleStatus = 'idle' | 'checking' | 'available' | 'taken' | 'same' | 'invalid' | 'rate-limited';

function maskContact(t: 'email' | 'phone', v: string): string {
  if (t === 'email') {
    const [local, domain] = v.split('@');
    if (!local || !domain) return v;
    return `${local.slice(0, 1)}${'•'.repeat(Math.max(local.length - 1, 1))}@${domain}`;
  }
  return v.length <= 4 ? v : `${v.slice(0, 2)}${'•'.repeat(v.length - 4)}${v.slice(-2)}`;
}

export function ProfileSettings(): JSX.Element {
  const me = useMeOrNull();
  const { invalidate } = useAuth();

  // Editable fields (separate from the contact, which has its own
  // dedicated flow).
  const [firstName, setFirstName] = useState(me?.firstName ?? '');
  const [lastName, setLastName] = useState<string>(me?.lastName ?? '');
  const [displayName, setDisplayName] = useState(me?.displayName ?? '');
  const [dob, setDob] = useState(me?.dob ?? '');
  const [gender, setGender] = useState<string>(me?.gender ?? '');
  const [country, setCountry] = useState(me?.country ?? 'US');
  const [state, setState] = useState<string>(me?.state ?? '');
  const [city, setCity] = useState(me?.city ?? '');

  // Sync local state when /me updates (after contact change, etc).
  useEffect(() => {
    if (!me) return;
    setFirstName(me.firstName);
    setLastName(me.lastName ?? '');
    setDisplayName(me.displayName);
    setDob(me.dob ?? '');
    setGender(me.gender ?? '');
    setCountry(me.country);
    setState(me.state ?? '');
    setCity(me.city ?? '');
  }, [me]);

  const [savedAt, setSavedAt] = useState<{ at: string; requestId: string } | null>(null);
  const [error, setError] = useState<{ message: string; requestId: string | null } | null>(null);
  const [saving, setSaving] = useState(false);

  // Handle change
  const [handle, setHandle] = useState(me?.handle ?? '');
  const [handleStatus, setHandleStatus] = useState<HandleStatus>('idle');
  const [stepUpForHandle, setStepUpForHandle] = useState(false);

  // Contact change
  const [stepUpForContact, setStepUpForContact] = useState(false);
  const [contactVerify, setContactVerify] = useState<{
    contact: { type: 'email' | 'phone'; value: string };
    sessionId: string;
  } | null>(null);

  // Handle availability check
  useEffect(() => {
    if (!handle || !me) { setHandleStatus('idle'); return; }
    if (!HANDLE_REGEX.test(handle)) { setHandleStatus('invalid'); return; }
    if (handle === me.handle) { setHandleStatus('same'); return; }
    setHandleStatus('checking');
    const t = setTimeout(() => {
      apiGet<{ available: boolean }>(`/api/v1/auth/handle-available?handle=${encodeURIComponent(handle)}`)
        .then((res) => {
          setHandleStatus(res.data.available ? 'available' : 'taken');
        })
        .catch((err: unknown) => {
          const apiErr = err as ApiError;
          setHandleStatus(apiErr?.code === 'RATE_LIMITED' ? 'rate-limited' : 'idle');
        });
    }, 300);
    return () => clearTimeout(t);
  }, [handle, me]);

  async function saveBasic(): Promise<void> {
    setError(null);
    setSavedAt(null);
    setSaving(true);
    try {
      const body: Record<string, unknown> = {};
      if (firstName !== me?.firstName) body.firstName = firstName;
      if (lastName !== (me?.lastName ?? '')) body.lastName = lastName || null;
      if (displayName !== me?.displayName) body.displayName = displayName;
      if (dob !== (me?.dob ?? '')) body.dob = dob || undefined;
      if (gender !== (me?.gender ?? '')) body.gender = gender || undefined;
      if (country !== me?.country) body.country = country;
      if (state !== (me?.state ?? '')) body.state = state || null;
      if (city !== (me?.city ?? '')) body.city = city || null;
      if (Object.keys(body).length === 0) {
        setSavedAt({ at: new Date().toISOString(), requestId: 'no-change' });
        return;
      }
      const res = await apiPut<{ user: UserMe }>('/api/v1/settings/profile', body);
      setSavedAt({ at: new Date().toISOString(), requestId: res.requestId });
      void invalidate();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.code === 'AGE_REQUIREMENT_NOT_MET') {
        setError({ message: 'You must be at least 18 years old.', requestId: apiErr.requestId ?? null });
      } else if (apiErr?.code === 'INVALID_REQUEST') {
        setError({ message: apiErr.message, requestId: apiErr.requestId ?? null });
      } else {
        setError({ message: apiErr?.message ?? 'Could not save profile.', requestId: apiErr.requestId ?? null });
      }
    } finally {
      setSaving(false);
    }
  }

  async function saveHandle(): Promise<void> {
    setError(null);
    if (handleStatus === 'taken') {
      setError({ message: 'Handle is already in use.', requestId: null });
      return;
    }
    if (handleStatus !== 'available') {
      setError({ message: 'Handle is not available yet — wait for the check to complete.', requestId: null });
      return;
    }
    setSaving(true);
    try {
      await apiPut<{ handle: string }>('/api/v1/user/handle', { handle });
      void invalidate();
      setStepUpForHandle(false);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.code === 'STEP_UP_REQUIRED') {
        setStepUpForHandle(true);
      } else if (apiErr?.code === 'HANDLE_UNAVAILABLE') {
        setError({ message: 'Handle is already in use.', requestId: apiErr.requestId ?? null });
      } else {
        setError({ message: apiErr?.message ?? 'Could not change handle.', requestId: apiErr.requestId ?? null });
      }
    } finally {
      setSaving(false);
    }
  }

  async function startContactChange(type: 'email' | 'phone', value: string): Promise<void> {
    setError(null);
    try {
      const res = await apiPost<OtpSession>('/api/v1/user/primary-contact/verify', {
        type,
        value,
      });
      setContactVerify({ contact: { type, value }, sessionId: res.data.sessionId ?? crypto.randomUUID() });
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.code === 'STEP_UP_REQUIRED') {
        setStepUpForContact(true);
        // Save the requested change and retry after step-up.
        pendingContactRef.current = { type, value };
      } else {
        setError({ message: apiErr?.message ?? 'Could not start contact change.', requestId: apiErr.requestId ?? null });
      }
    }
  }

  const pendingContactRef = useRef<{ type: 'email' | 'phone'; value: string } | null>(null);

  async function commitContactChange(receiptId: string): Promise<void> {
    if (!contactVerify) return;
    setError(null);
    try {
      await apiPut<{ ok: boolean }>('/api/v1/user/primary-contact', {
        type: contactVerify.contact.type,
        value: contactVerify.contact.value,
        receiptId,
      });
      setContactVerify(null);
      void invalidate();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.code === 'RECEIPT_INVALID') {
        setError({ message: 'This code is no longer valid. Start the contact change again.', requestId: apiErr.requestId ?? null });
        setContactVerify(null);
      } else {
        setError({ message: apiErr?.message ?? 'Could not change contact.', requestId: apiErr.requestId ?? null });
      }
    }
  }

  if (!me) return <></>;

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-bold">Profile</h1>

      <form
        onSubmit={(e) => { e.preventDefault(); void saveBasic(); }}
        className="mt-6 space-y-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <FormField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" required />
          <FormField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" optional />
        </div>
        <FormField label="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} helpText="Shown to other users. Not unique." required />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Date of birth" type="date" value={dob} onChange={(e) => setDob(e.target.value)} max={todayMinus18()} required />
          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-gray-800 dark:text-gray-200">Gender <span className="ml-1 text-xs font-normal text-gray-500">(optional)</span></label>
            <select id="gender" value={gender} onChange={(e) => setGender(e.target.value)} className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
              <option value="">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Country" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} helpText="ISO 3166-1 alpha-2" required />
          <FormField label="State" value={state} onChange={(e) => setState(e.target.value)} optional />
          <FormField label="City" value={city} onChange={(e) => setCity(e.target.value)} optional />
        </div>

        {error ? <Alert variant="error" requestId={error.requestId}>{error.message}</Alert> : null}
        {savedAt ? <Alert variant="success">Saved.</Alert> : null}

        <Button type="submit" isLoading={saving}>Save</Button>
      </form>

      <hr className="my-8 border-gray-200 dark:border-gray-800" />

      <h2 className="text-lg font-semibold">Handle</h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Your unique @handle. Changing it is a security-sensitive action.
      </p>
      <div className="mt-3 flex items-end gap-2">
        <FormField
          label="@Handle"
          value={handle}
          onChange={(e) => setHandle(e.target.value.toLowerCase())}
          helpText={
            handleStatus === 'available' ? 'Available ✓'
            : handleStatus === 'taken' ? 'Already in use.'
            : handleStatus === 'same' ? 'Same as current.'
            : handleStatus === 'invalid' ? 'Invalid format.'
            : handleStatus === 'rate-limited' ? 'Rate-limited; we\'ll check on submit.'
            : handleStatus === 'checking' ? 'Checking…'
            : '3–30 lowercase letters/digits/underscore, starts with a letter.'
          }
        />
        <Button
          onClick={() => void saveHandle()}
          isLoading={saving}
          disabled={handle === me.handle || handleStatus !== 'available'}
        >
          Update handle
        </Button>
      </div>

      <hr className="my-8 border-gray-200 dark:border-gray-800" />

      <h2 className="text-lg font-semibold">Email & phone</h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        We use these to sign you in. Changing either is a security-sensitive action.
      </p>
      <div className="mt-3 space-y-2">
        <ContactRow
          label="Email"
          value={me.email}
          verified={me.emailVerified}
          onChangeClick={() => {
            const next = window.prompt('New email address:');
            if (next && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
              void startContactChange('email', next);
            } else if (next) {
              window.alert('That doesn\'t look like an email address.');
            }
          }}
        />
        <ContactRow
          label="Phone"
          value={me.phone}
          verified={me.phoneVerified}
          onChangeClick={() => {
            const next = window.prompt('New phone (E.164, e.g. +15555550123):');
            if (next && /^\+\d{6,15}$/.test(next)) {
              void startContactChange('phone', next);
            } else if (next) {
              window.alert('That doesn\'t look like a valid phone (use +countrycode format).');
            }
          }}
        />
      </div>

      {/* Step-up modal for handle change */}
      <StepUpModal
        open={stepUpForHandle}
        onClose={() => setStepUpForHandle(false)}
        onSuccess={() => {
          // Re-attempt the handle change after the user proves
          // recency.
          void saveHandle();
        }}
        contact={{
          type: me.email ? 'email' : 'phone',
          value: me.email ?? me.phone ?? '',
        }}
      />

      {/* Step-up modal for contact change */}
      <StepUpModal
        open={stepUpForContact}
        onClose={() => {
          setStepUpForContact(false);
          pendingContactRef.current = null;
        }}
        onSuccess={() => {
          setStepUpForContact(false);
          const pending = pendingContactRef.current;
          pendingContactRef.current = null;
          if (pending) void startContactChange(pending.type, pending.value);
        }}
        contact={{
          type: me.email ? 'email' : 'phone',
          value: me.email ?? me.phone ?? '',
        }}
      />

      {/* Contact change: verify the OTP for the new contact */}
      {contactVerify ? (
        <div className="mt-6 rounded border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            We sent a 6-digit code to your new {contactVerify.contact.type}{' '}
            <span className="font-mono">{maskContact(contactVerify.contact.type, contactVerify.contact.value)}</span>.
            Enter it below to commit the change.
          </p>
          <div className="mt-3">
            <OtpVerifyPanel
              mode="signin"
              sessionId={contactVerify.sessionId}
              contact={contactVerify.contact}
              onSuccess={({ receiptId }) => {
                if (receiptId) void commitContactChange(receiptId);
              }}
              onSwitchContact={() => setContactVerify(null)}
              onRestart={() => setContactVerify(null)}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ContactRow({
  label, value, verified, onChangeClick,
}: {
  label: string; value: string | null; verified: boolean; onChangeClick: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-800 dark:bg-gray-900">
      <div>
        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
        <div className="font-mono">{value ?? <span className="text-gray-400">none</span>}</div>
        {value && !verified ? (
          <div className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">unverified</div>
        ) : null}
      </div>
      <Button variant="secondary" size="sm" onClick={onChangeClick}>Change</Button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Module-level mutable ref for the pending contact value across the
// step-up flow. There is only one ProfileSettings instance ever
// rendered; using a ref avoids a re-render of the form when this
// changes (the step-up modal's success handler is the only consumer).
// -----------------------------------------------------------------------------
